import React from 'react';
import apiClient from '../config/api';
import { getExcelPreviewDisabledReason, isWordFile } from '../utils/documentPreview';

  const FileTable = ({
  uploadedFiles,
  styles,
  formatDate,
  getCategoryName,
  getTypeName,
  setShowDescription,
  setShowFilename,
  confirmDelete,
  setStatusModalFile,
  setNewStatusId,
  totalPages,
  currentPage,
  handleApplyFilters,
  sortConfig,
  onSort,
  isRefreshing,
  isLoadingPage,
  isTableLoading,
  isBulkMode,
  deleteEligibility,
  isCheckingDeleteEligibility,
  selectedFileIds,
  onToggleSelectFile,
  onSelectAllCurrentPage,
  isBulkUpdating,
  onMarkViewed,
  onViewFile,
  isCA,
  isAdmin
}) => {
  const safeSelectedIds = Array.isArray(selectedFileIds) ? selectedFileIds : [];
  const markViewed = typeof onMarkViewed === 'function' ? onMarkViewed : () => {};
  const compactTh = { ...styles.th, padding: '15px 11px', fontSize: '14px' };
  const compactTd = { ...styles.td, padding: '15px 12px', fontSize: '14px', backgroundColor: 'inherit' };
  const truncateCharacters = (value, limit = 15) => {
    const text = value === null || value === undefined ? '' : String(value).trim();
    return text.length > limit ? `${text.slice(0, limit)}...` : text;
  };

  const downloadFile = async (file) => {
    try {
      const response = await apiClient.get(`/files/uploads/${file.FileID}/download`, {
        responseType: 'blob',
        withCredentials: true
      });
      const objectUrl = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = file.FileName || `document-${file.FileID}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch (error) {
      console.error('Failed to download document:', error);
    }
  };

  const ViewedIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="#8B8EF2" strokeWidth="2" fill="none" />
      <path d="M8 12.5l2.5 2.5 5-5" stroke="#8B8EF2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const UnreadIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 13.2c2.5-3.2 5.5-4.9 8-4.9s5.5 1.7 8 4.9"
        stroke="#8B8EF2"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="13.4" r="2.4" fill="#8B8EF2" />
    </svg>
  );

  const DownloadIcon = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3.5v10" />
      <path d="m8.25 10.5 3.75 3.75 3.75-3.75" />
      <path d="M5 18.5h14" />
    </svg>
  );

  const TrashIcon = () => (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 7h16" />
      <path d="M9 7V4h6v3" />
      <path d="m6.5 7 1 13h9l1-13" />
      <path d="M10 11v5M14 11v5" />
    </svg>
  );

  const renderSortableHeader = (label, key) => {
    const isActive = sortConfig?.field === key;
    const indicator = isActive ? (sortConfig.direction === 'asc' ? '▲' : '▼') : '▲▼';

    return (
      <th
        style={{ ...compactTh, cursor: 'pointer', textAlign: 'center' }}
        onClick={() => onSort(key)}
        title="Sort"
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', textTransform: 'uppercase', letterSpacing: '0.02em' }}>
          <span>{label}</span>
          <span style={{ fontSize: '0.85em', opacity: isActive ? 1 : 0.6 }}>{indicator}</span>
        </div>
      </th>
    );
  };

  const toDisplayUpper = (value) => {
    const normalized = value === null || value === undefined ? '' : String(value).trim();
    return normalized ? normalized.toUpperCase() : '';
  };

  const canBulkDelete = file => Number(file.StatusID) === 1 && deleteEligibility?.[file.FileID] === true;
  const currentPageIds = uploadedFiles.map(file => file.FileID);
  const allSelectedOnPage = isBulkMode && currentPageIds.length > 0 && currentPageIds.every(id => safeSelectedIds.includes(id));

  if (isTableLoading) {
    return (
      <div style={styles.noRecordsContainer}>
        <div style={styles.noRecordsMessage}>Loading records...</div>
      </div>
    );
  }

  if (uploadedFiles.length === 0) {
    return (
      <div style={styles.noRecordsContainer}>
        <div style={styles.noRecordsMessage}>No records found</div>
      </div>
    );
  }

  return (
    <div
      style={{
        ...styles.recordsContainer,
        opacity: isRefreshing || isLoadingPage ? 0.3 : 1,
        transition: 'opacity 0.10s ease'
      }}
    >
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              {isBulkMode && (
                <th style={{ ...compactTh, textAlign: 'center' }}>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', userSelect: 'none', color: 'inherit' }}>
                    <input
                      type="checkbox"
                      checked={allSelectedOnPage}
                      onChange={() => onSelectAllCurrentPage(currentPageIds)}
                      disabled={isBulkUpdating || isCheckingDeleteEligibility || currentPageIds.length === 0}
                      aria-label="Select all on current page"
                      style={{ transform: 'scale(1.05)' }}
                    />
                    <span style={{ fontSize: '12px', fontWeight: 600, color: 'inherit', letterSpacing: '0.02em' }}>(Select all)</span>
                  </label>
                </th>
              )}
              {renderSortableHeader('File ID', 'fileId')}
              {renderSortableHeader('Date', 'date')}
              {renderSortableHeader('User', 'user')}
              {renderSortableHeader('Category', 'category')}
              {renderSortableHeader('Type', 'type')}
               <th style={{ ...compactTh, textAlign: 'center', textTransform: 'uppercase' }}>Description</th>
              <th style={{ ...compactTh, textAlign: 'center', textTransform: 'uppercase' }}>Filename</th>
              {renderSortableHeader('County', 'county')}
              {renderSortableHeader('Status', 'status')}
              <th style={{ ...compactTh, textAlign: 'center', textTransform: 'uppercase' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {uploadedFiles.map((file, index) => {
              const previewDisabledReason = getExcelPreviewDisabledReason(file);
              const isWordDocument = isWordFile(file);
              return (
              <tr
                key={index}
                style={{
                  backgroundColor: isBulkMode && isAdmin && !isCheckingDeleteEligibility && !canBulkDelete(file) ? '#fff1f2' : '#fff',
                  color: isBulkMode && isAdmin && !isCheckingDeleteEligibility && !canBulkDelete(file) ? '#9f1239' : undefined
                }}
                title={isBulkMode && isAdmin && !isCheckingDeleteEligibility && !canBulkDelete(file)
                  ? (Number(file.StatusID) !== 1
                    ? 'Cannot delete: status must be New'
                    : 'Cannot delete: this file has an assigned owner')
                  : undefined}
              >
                {isBulkMode && (
                  <td style={{ ...compactTd, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={safeSelectedIds.includes(file.FileID)}
                      onChange={() => onToggleSelectFile(file.FileID)}
                      disabled={isBulkUpdating || isCheckingDeleteEligibility}
                      aria-label={`Select file ${file.FileID}`}
                    />
                  </td>
                )}
                <td style={{ ...compactTd, textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    <span style={isAdmin && Boolean(file.IsAdminOnly) ? { color: '#e11d48', fontWeight: 700 } : {}}>{file.FileID}</span>
                    {isAdmin && Boolean(file.IsAdminOnly) && (
                      <em style={{ color: '#e11d48', fontSize: 16, fontStyle: 'italic', fontWeight: 700 }}>Int</em>
                    )}
                  </div>
                </td>
                <td style={{ ...compactTd, whiteSpace: 'nowrap' }}>{formatDate(file.UploadDate)}</td>
                <td style={compactTd}>{file.UploadedBy || '—'}</td>
                <td style={compactTd}>{getCategoryName(file.AttachmentCategoryID)}</td>
                <td style={compactTd}>{getTypeName(file.AttachmentTypeID)}</td>
                <td style={compactTd}>
                  {truncateCharacters(toDisplayUpper(file.Description))}
                  {file.Description && file.Description.trim().length > 15 && (
                    <span
                      onClick={() => setShowDescription(file)}
                      style={styles.showMoreLink}
                    >
                      Show more
                    </span>
                  )}
                </td>
                <td style={compactTd}>
                  {file.FileName ? truncateCharacters(file.FileName) : '—'}
                  {file.FileName && file.FileName.trim().length > 15 && (
                    <span
                      onClick={() => setShowFilename(file)}
                      style={styles.showMoreLink}
                    >
                      Show more
                    </span>
                  )}
                </td>
                <td style={compactTd}>{file.County || '—'}</td>
                <td style={compactTd}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>{file.Status || '—'}</span>
                    <button
                      onClick={() => {
                        setStatusModalFile(file);
                        setNewStatusId(file.StatusID);
                      }}
                      style={{ ...styles.editStatusIcon, display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'transparent', border: 'none', padding: 0 }}
                      title="Edit Status"
                      aria-label="Edit status"
                    >
                      <span aria-hidden="true">&#x1F589;</span>
                    </button>
                  </div>
                </td>
                <td style={compactTd}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                    {!isWordDocument && <button
                      type="button"
                      disabled={Boolean(previewDisabledReason)}
                      style={{
                        ...styles.viewButton,
                        width: 34,
                        minWidth: 34,
                        height: 34,
                        padding: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '18px',
                        lineHeight: 1,
                        opacity: previewDisabledReason ? 0.5 : 1,
                        cursor: previewDisabledReason ? 'not-allowed' : 'pointer'
                      }}
                      onClick={() => {
                        if (isCA && file.HasViewFlag) {
                          markViewed(file.FileID);
                        }
                        onViewFile(file);
                      }}
                      aria-label={previewDisabledReason || `Open ${file.FileName || `file ${file.FileID}`}`}
                      title={previewDisabledReason || 'Open document'}
                    >
                      <span aria-hidden="true">↗</span>
                    </button>}
                    <button
                      type="button"
                      onClick={() => downloadFile(file)}
                      style={{
                        ...styles.viewButton,
                        width: 36,
                        minWidth: 36,
                        height: 34,
                        padding: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxSizing: 'border-box'
                      }}
                      aria-label={`Download ${file.FileName || `file ${file.FileID}`}`}
                      title="Download"
                    >
                      <DownloadIcon />
                    </button>
                    {isCA && file.HasViewFlag && (
                      <span
                        title={file.IsViewedByUser ? 'Viewed' : 'Not viewed yet'}
                        aria-label={file.IsViewedByUser ? 'Viewed' : 'Not viewed yet'}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 26,
                          height: 22
                        }}
                      >
                        {file.IsViewedByUser ? <UnreadIcon /> : <ViewedIcon />}
                      </span>
                    )}
                    <button
                      onClick={() => confirmDelete(file.FileID)}
                      style={{
                        ...styles.deleteButton,
                        width: 34,
                        minWidth: 34,
                        height: 34,
                        padding: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      aria-label={`Delete ${file.FileName || `file ${file.FileID}`}`}
                      title="Delete document"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div style={styles.paginationWrapper}>
            <ul style={styles.pagination}>
              <li>
                <button
                  style={styles.pageButton}
                  onClick={() => handleApplyFilters(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  &lt;
                </button>
              </li>

              {Array.from({ length: totalPages }).map((_, index) => {
                const page = index + 1;
                if (
                  page === 1 ||
                  page === totalPages ||
                  Math.abs(page - currentPage) <= 1
                ) {
                  return (
                    <li key={page}>
                      <button
                        style={{
                          ...styles.pageButton,
                          ...(currentPage === page ? styles.activePage : {})
                        }}
                        onClick={() => handleApplyFilters(page)}
                      >
                        {page}
                      </button>
                    </li>
                  );
                } else if (
                  (page === currentPage - 2 && page > 1) ||
                  (page === currentPage + 2 && page < totalPages)
                ) {
                  return (
                    <li key={page}>
                      <span style={styles.ellipsis}>…</span>
                    </li>
                  );
                }
                return null;
              })}

              <li>
                <button
                  style={styles.pageButton}
                  onClick={() => handleApplyFilters(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  &gt;
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileTable;
